import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { env } from '@/lib/env';

/**
 * Operator funnel dashboard — P1.12.
 *
 * Pulls counts from /api/operator/funnel/summary. Shows the four D1-
 * derivable stages (claim-links / wizard submits / activate clicks /
 * checkout completes) plus current active subscriptions and tenant
 * status breakdown.
 *
 * Window: 30 days default; togglable to 90.
 *
 * GA4-only stages (directory click → /start landing, preview viewed)
 * are linked out to the Google Analytics console — this is not a
 * unified analytics surface, it's the source of truth for revenue/
 * activation metrics.
 */
export const Route = createFileRoute('/_app/operator/funnel')({
  staticData: { crumb: 'Funnel' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorFunnel,
});

interface FunnelSummary {
  window_days: number;
  since_ts: number;
  funnel: {
    claim_links_minted: number;
    onboarding_submitted: number;
    activate_started: number;
    checkout_completed: number;
  };
  subscriptions: {
    active: number;
    trialing: number;
  };
  tenants: {
    total_new_in_window: number;
    by_status: Record<string, number>;
  };
  founder_promo: {
    max_redemptions: number;
    seats_taken: number;
    seats_left: number;
  };
}

function OperatorFunnel() {
  const [windowDays, setWindowDays] = React.useState<30 | 90>(30);

  const summary = useQuery({
    queryKey: ['operator', 'funnel', windowDays],
    queryFn: async (): Promise<FunnelSummary> => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/funnel/summary?window=${windowDays}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`funnel_${res.status}`);
      return (await res.json()) as FunnelSummary;
    },
    refetchInterval: 60_000,
  });

  if (summary.isError) {
    return (
      <div className="space-y-4">
        <Header windowDays={windowDays} setWindowDays={setWindowDays} />
        <Card>
          <CardContent className="flex items-center gap-3 p-6 text-sm text-text-muted">
            <AlertTriangle className="size-4 text-amber-500" aria-hidden="true" />
            Nie udało się pobrać danych: {(summary.error as Error).message}
          </CardContent>
        </Card>
      </div>
    );
  }

  const data = summary.data;
  const f = data?.funnel;
  const s = data?.subscriptions;
  const t = data?.tenants;
  const fp = data?.founder_promo;

  const conv = (numerator: number | undefined, denominator: number | undefined) => {
    if (!denominator || denominator === 0) return '—';
    return `${Math.round(((numerator ?? 0) / denominator) * 1000) / 10}%`;
  };

  return (
    <div className="space-y-6">
      <Header windowDays={windowDays} setWindowDays={setWindowDays} />

      {fp ? (
        <Card className="ring-2 ring-accent/40">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-accent">
                Founder-50 promo
              </div>
              <div className="mt-1 font-display text-2xl font-semibold text-text">
                {fp.seats_left} z {fp.max_redemptions} miejsc wolnych
              </div>
              <div className="mt-0.5 text-xs text-text-muted">
                Pierwsze {fp.max_redemptions} klinik na planie rocznym dostaje 12 miesięcy gratis (kupon{' '}
                <span className="font-mono">FOUNDER_50_YEAR_FREE</span> stosowany automatycznie w
                checkout).
              </div>
            </div>
            <div className="text-xs text-text-muted">
              <span className="font-display text-base font-semibold text-text tabular-nums">
                {fp.seats_taken}
              </span>{' '}
              już zajęte
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 md:grid-cols-4">
        <Stage
          label="Claim links minted"
          value={f?.claim_links_minted ?? 0}
          subtitle="directory CTA target"
        />
        <Stage
          label="Onboarding submitted"
          value={f?.onboarding_submitted ?? 0}
          subtitle={`${conv(f?.onboarding_submitted, f?.claim_links_minted)} of claim links`}
        />
        <Stage
          label="Activate clicked"
          value={f?.activate_started ?? 0}
          subtitle={`${conv(f?.activate_started, f?.onboarding_submitted)} of submits`}
        />
        <Stage
          label="Checkout completed"
          value={f?.checkout_completed ?? 0}
          subtitle={`${conv(f?.checkout_completed, f?.activate_started)} of activates`}
          highlight
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Subskrypcje (live)</CardTitle>
            <CardDescription>
              Cumulative — niezależnie od okna czasowego.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-text-subtle">Active</div>
              <div className="mt-1 font-display text-3xl font-semibold text-text tabular-nums">
                {s?.active ?? 0}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-text-subtle">Trialing</div>
              <div className="mt-1 font-display text-3xl font-semibold text-text tabular-nums">
                {s?.trialing ?? 0}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenants by status</CardTitle>
            <CardDescription>
              Nowi w oknie: {t?.total_new_in_window ?? 0}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(t?.by_status ?? {})
                .sort()
                .map(([status, count]) => (
                  <li key={status} className="flex items-center justify-between">
                    <span className="text-text-muted">{status}</span>
                    <span className="font-display text-base font-semibold text-text tabular-nums">
                      {count}
                    </span>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>External funnel stages</CardTitle>
          <CardDescription>
            Te etapy nie żyją w D1 — przejdź do Google Analytics / Meta Events Manager.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-text-muted">
            <li>• Directory impressions — Google Search Console (porownajdentyste, porownajweterynarza)</li>
            <li>• /start landing (page_view) — GA4 → Events</li>
            <li>• Preview viewed (custom event) — GA4 → Events → preview_viewed</li>
            <li>• Stripe MRR / churn — Stripe Dashboard → Billing → Subscriptions</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Header({
  windowDays,
  setWindowDays,
}: {
  windowDays: number;
  setWindowDays: (n: 30 | 90) => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-text">
          <TrendingUp className="size-5 text-accent" aria-hidden="true" />
          Funnel — last {windowDays} days
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Conversion rates per stage. Reload co 60 s.
        </p>
      </div>
      <div className="flex items-center gap-1 rounded-full bg-surface p-1 text-xs ring-1 ring-border">
        {[30, 90].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setWindowDays(n as 30 | 90)}
            className={
              windowDays === n
                ? 'rounded-full bg-text px-3 py-1 font-medium text-bg'
                : 'rounded-full px-3 py-1 text-text-muted hover:text-text'
            }
          >
            {n} dni
          </button>
        ))}
      </div>
    </div>
  );
}

function Stage({
  label,
  value,
  subtitle,
  highlight,
}: {
  label: string;
  value: number;
  subtitle?: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'ring-2 ring-accent/40' : undefined}>
      <CardContent className="p-5">
        <div className="text-xs uppercase tracking-wide text-text-subtle">{label}</div>
        <div className="mt-1 font-display text-3xl font-semibold text-text tabular-nums">
          {value}
        </div>
        {subtitle ? <div className="mt-1 text-xs text-text-muted">{subtitle}</div> : null}
      </CardContent>
    </Card>
  );
}
